import {
  type FC,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { z } from "zod";

import { ChevronRightIcon } from "../../common/icons";
import { CompositionContextHeader, ReplayPrefixBand } from "./contextHeader";
import {
  type ContentBlockEntity,
  type GenerationStatsResponse,
  type GenerationStatsState,
  type MessageEntity,
  type MetaAccent,
  type SnippetEntity,
  type SourceKind,
  type StepDetail,
  type StepEntity,
  LOCK_WAIT_VISIBLE_MS,
  fmtDuration,
  fmtTokens,
  formatCacheMarkerValid,
  formatStepErrorLabel,
  getMetaAccentClass,
  getReplayContextSummary,
  modelRefTail,
  shortSha,
  totalWallClockMs,
} from "./observabilityModel";
import { Overlay } from "./Overlays";
import { STEP_KIND_LABELS } from "./WakeRow";

const generationStatsResponseSchema = z.custom<GenerationStatsResponse>();

export const StepDetailOverlay: FC<{
  detail: StepDetail;
  onClose: () => void;
  onLoadSnippet: (sha: string) => Promise<SnippetEntity | null>;
  onOpenSnippet: (sha: string) => void;
}> = ({ detail, onClose, onLoadSnippet, onOpenSnippet }) => {
  const [generationStats, setGenerationStats] = useState<GenerationStatsState>(
    () => initialGenerationStatsState(detail.step.openrouterStatsJson),
  );
  useEffect(() => {
    setGenerationStats(
      initialGenerationStatsState(detail.step.openrouterStatsJson),
    );
  }, [detail.step.id, detail.step.openrouterStatsJson]);

  const maxSha = useMemo(() => {
    // Compute cache boundary locally (the server returns it but we recompute
    // In case the overlay is opened on a stale detail).
    let idx: number | null = null;
    for (const m of detail.messages) {
      if (m.cacheControl) {
        idx = idx === null ? m.sequence : Math.max(idx, m.sequence);
      }
    }
    return idx;
  }, [detail.messages]);
  const replay = getReplayContextSummary(detail.step);

  const fetchGenerationStats = useCallback(async () => {
    setGenerationStats({ status: "loading" });
    try {
      const resp = await fetch(
        `/user/llm-calls/${detail.step.id}/generation-stats`,
        { credentials: "include" },
      );
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const json = generationStatsResponseSchema.parse(await resp.json());
      if (json.status === "expired") {
        setGenerationStats({ message: json.message, status: "expired" });
        return;
      }
      setGenerationStats({
        cached: json.cached,
        stats: json.stats,
        status: "ready",
      });
    } catch (error) {
      setGenerationStats({
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch generation stats",
        status: "error",
      });
    }
  }, [detail.step.id]);

  return (
    <Overlay
      onClose={onClose}
      title={`Step #${detail.step.stepIndexInWake} — ${STEP_KIND_LABELS[detail.step.stepKind]}`}
    >
      <StepMetaGrid step={detail.step} />
      <ProviderErrorBodySection body={detail.step.providerErrorBody} />
      <GenerationStatsSection
        generationId={detail.step.openrouterGenerationId}
        onFetch={fetchGenerationStats}
        state={generationStats}
      />
      <div className="border-t border-slate-100 dark:border-slate-800">
        <CompositionContextHeader
          cacheBoundaryIndex={maxSha}
          localRowCount={detail.messages.length}
          replay={replay}
        />
        <ReplayPrefixBand replay={replay} />
        <BlockStack
          messages={detail.messages}
          cacheBoundaryIndex={maxSha}
          onLoadSnippet={onLoadSnippet}
          onOpenSnippet={onOpenSnippet}
        />
      </div>
    </Overlay>
  );
};

export const ProviderErrorBodySection: FC<{ body: string | null }> = ({
  body,
}) => {
  if (!body) {
    return null;
  }
  return (
    <section className="border-t border-rose-100 bg-rose-50/50 px-4 py-3 dark:border-rose-900/50 dark:bg-rose-950/20">
      <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-rose-500">
        provider error body
      </div>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded border border-rose-100 bg-white/80 p-2 font-mono text-[11px] text-rose-950 dark:border-rose-900 dark:bg-slate-950/60 dark:text-rose-100">
        {body}
      </pre>
    </section>
  );
};

export const StepMetaGrid: FC<{ step: StepEntity }> = ({ step }) => {
  const cr = step.cacheReadTokens;
  const cw = step.cacheWriteTokens;
  const input = step.inputTokens;
  const nc =
    step.noCacheTokens ??
    (input === null ? null : Math.max(input - (cr ?? 0) - (cw ?? 0), 0));
  // 2 columns on narrow viewports (drawer pinned to mobile), 4 on desktop.
  // Fixed at 4 cols forced horizontal overflow when the drawer was a mobile
  // Full-width sheet and the 12-cell grid couldn't fit at 10px labels.
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 px-4 py-3 font-mono text-[10px] sm:grid-cols-4">
      <MetaCell label="input" value={fmtTokens(input)} />
      <MetaCell label="cache read" value={fmtTokens(cr)} accent="emerald" />
      <MetaCell label="cache write" value={fmtTokens(cw)} accent="amber" />
      <MetaCell
        label="no-cache"
        value={fmtTokens(nc)}
        accent={nc !== null && nc > 0 ? "rose" : undefined}
      />
      <MetaCell label="output" value={fmtTokens(step.outputTokens)} />
      <MetaCell
        label="turn"
        value={fmtDuration(step.durationMs)}
        hint="generateText wall-clock"
      />
      <MetaCell
        label="lock wait"
        value={step.lockWaitMs === null ? "—" : fmtDuration(step.lockWaitMs)}
        accent={
          typeof step.lockWaitMs === "number" &&
          step.lockWaitMs >= LOCK_WAIT_VISIBLE_MS
            ? "amber"
            : undefined
        }
        hint="per-channel mutex wait before generateText"
      />
      <MetaCell
        label="total"
        value={fmtDuration(totalWallClockMs(step.durationMs, step.lockWaitMs))}
        hint="lock wait + turn"
      />
      <MetaCell label="finish" value={step.finishReason ?? "—"} />
      <MetaCell
        label="error"
        value={step.errorKind === null ? "—" : formatStepErrorLabel(step)}
        accent={step.errorKind === null ? undefined : "rose"}
        hint={step.errorText ?? undefined}
      />
      <MetaCell
        label="cache valid"
        value={formatCacheMarkerValid(step.cacheMarkerValid)}
        accent={step.cacheMarkerValid === false ? "rose" : undefined}
      />
      <MetaCell label="prefix sha" value={shortSha(step.prefixSha, 8)} />
      <MetaCell label="sys sha" value={shortSha(step.systemPromptSha, 8)} />
      <MetaCell label="tools sha" value={shortSha(step.toolsSha, 8)} />
      <MetaCell label="model" value={modelRefTail(step.modelRef)} />
    </div>
  );
};

export const MetaCell: FC<{
  label: string;
  value: string;
  accent?: MetaAccent;
  hint?: string;
}> = ({ label, value, accent, hint }) => {
  const accentClass = getMetaAccentClass(accent);
  return (
    <div className="flex flex-col gap-0.5" title={hint}>
      <span className="uppercase tracking-widest text-slate-400 text-[9px]">
        {label}
      </span>
      <span className={`truncate ${accentClass}`}>{value}</span>
    </div>
  );
};

export function initialGenerationStatsState(
  statsJson: string | null,
): GenerationStatsState {
  if (!statsJson) {
    return { status: "idle" };
  }
  try {
    const stats = JSON.parse(statsJson);
    return isPlainStatsObject(stats)
      ? { cached: true, stats, status: "ready" }
      : {
          message: "Stored generation stats JSON is invalid.",
          status: "error",
        };
  } catch {
    return {
      message: "Stored generation stats JSON is invalid.",
      status: "error",
    };
  }
}

export const GenerationStatsSection: FC<{
  generationId: string | null;
  onFetch: () => void;
  state: GenerationStatsState;
}> = ({ generationId, onFetch, state }) => {
  if (!generationId) {
    return null;
  }
  return (
    <div className="border-t border-cyan-500/20 bg-cyan-500/[0.03] px-4 py-3 dark:border-cyan-400/10">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-cyan-700 dark:text-cyan-300">
          Generation stats
        </span>
        <span className="truncate font-mono text-[10px] text-slate-400">
          {generationId}
        </span>
        {state.status === "idle" && (
          <button
            type="button"
            onClick={onFetch}
            className="ml-auto rounded-sm border border-cyan-500/30 px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-widest text-cyan-700 hover:bg-cyan-500/10 dark:border-cyan-400/30 dark:text-cyan-300"
          >
            Fetch from OpenRouter
          </button>
        )}
        {state.status === "loading" && (
          <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-cyan-700 dark:text-cyan-300">
            fetching…
          </span>
        )}
      </div>
      {state.status === "ready" && <GenerationStatsReady stats={state.stats} />}
      {state.status === "expired" && (
        <p className="mt-2 font-mono text-[10px] text-amber-700 dark:text-amber-300">
          {state.message}
        </p>
      )}
      {state.status === "error" && (
        <p className="mt-2 font-mono text-[10px] text-rose-700 dark:text-rose-300">
          {state.message}
        </p>
      )}
    </div>
  );
};

export const GenerationStatsReady: FC<{ stats: object }> = ({ stats }) => (
  <div className="mt-3">
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 font-mono text-[10px] sm:grid-cols-5">
      <MetaCell
        label="native prompt"
        value={formatStatNumber(readNumberStat(stats, "native_tokens_prompt"))}
      />
      <MetaCell
        label="native completion"
        value={formatStatNumber(
          readNumberStat(stats, "native_tokens_completion"),
        )}
      />
      <MetaCell
        label="total cost"
        value={formatUsdStat(readNumberStat(stats, "total_cost"))}
        accent="emerald"
      />
      <MetaCell
        label="cache discount"
        value={formatUsdStat(
          readNumberStat(stats, "cache_discount") ??
            readNumberStat(stats, "usage_cache"),
        )}
        accent="amber"
      />
      <MetaCell
        label="finish"
        value={readStringStat(stats, "finish_reason") ?? "—"}
      />
    </div>
    <details className="mt-2">
      <summary className="cursor-pointer font-mono text-[9px] uppercase tracking-widest text-slate-500">
        view raw
      </summary>
      <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded border border-slate-200 bg-white/60 p-2 font-mono text-[10px] text-slate-700 dark:border-slate-800 dark:bg-slate-950/60 dark:text-slate-300">
        {JSON.stringify(stats, null, 2)}
      </pre>
    </details>
  </div>
);

function isPlainStatsObject(stats: unknown): stats is object {
  return Boolean(stats && typeof stats === "object" && !Array.isArray(stats));
}

function readNumberStat(stats: object, key: string): number | null {
  const value = Reflect.get(stats, key);
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readStringStat(stats: object, key: string): string | null {
  const value = Reflect.get(stats, key);
  return typeof value === "string" && value.trim() ? value : null;
}

function formatStatNumber(value: number | null): string {
  return value === null ? "—" : value.toLocaleString();
}

function formatUsdStat(value: number | null): string {
  return value === null ? "—" : `$${value.toFixed(6)}`;
}

// ---------------------------------------------------------------------------
// Block stack — the composition view. Width of bar = relative presence.
// Cache boundary rendered as a horizontal amber line.
// ---------------------------------------------------------------------------

const SOURCE_KIND_COLORS: Record<SourceKind, string> = {
  assistant_output: "bg-sky-300 dark:bg-sky-700",
  compact_seed: "bg-rose-300 dark:bg-rose-700",
  compact_tail: "bg-rose-200 dark:bg-rose-800",
  mailbox_drain: "bg-violet-300 dark:bg-violet-700",
  phase_prompt: "bg-amber-200 dark:bg-amber-800",
  resumed_history: "bg-slate-300 dark:bg-slate-600",
  runtime_inject_delivered_tool_checkpoint: "bg-cyan-300 dark:bg-cyan-700",
  runtime_inject_episodic: "bg-emerald-200 dark:bg-emerald-800",
  runtime_inject_growth_nudge: "bg-lime-300 dark:bg-lime-800",
  runtime_inject_semantic: "bg-emerald-300 dark:bg-emerald-700",
  runtime_inject_void_text_nudge: "bg-orange-300 dark:bg-orange-800",
  static_system: "bg-slate-400 dark:bg-slate-500",
  tool_definitions: "bg-slate-400 dark:bg-slate-500",
  tool_result: "bg-sky-200 dark:bg-sky-800",
  wake_header: "bg-violet-200 dark:bg-violet-800",
};

export const BlockStack: FC<{
  messages: MessageEntity[];
  cacheBoundaryIndex: number | null;
  onLoadSnippet: (sha: string) => Promise<SnippetEntity | null>;
  onOpenSnippet: (sha: string) => void;
}> = ({ messages, cacheBoundaryIndex, onLoadSnippet, onOpenSnippet }) => {
  // Width of each bar = its share of total content-block count. This is a
  // Rough proxy for size since we don't have per-message token counts at the
  // Composition tier (by design — tier 3 fetches them). Proportional widths
  // Make "dominant blocks" visible at a glance even without exact numbers.
  const weights = messages.map((m) => Math.max(m.blocks.length, 1));
  const maxWeight = Math.max(...weights, 1);

  return (
    <div className="flex flex-col">
      {messages.map((msg, i) => {
        const widthPct = (weights[i] / maxWeight) * 100;
        const colorClass = SOURCE_KIND_COLORS[msg.sourceKind];
        const below =
          cacheBoundaryIndex !== null && msg.sequence > cacheBoundaryIndex;
        const onClick = msg.textSha
          ? () => onOpenSnippet(msg.textSha!)
          : undefined;
        const reasoningBlocks = msg.blocks.filter(
          (block) => block.type === "reasoning" && block.textSha !== null,
        );
        return (
          <div key={msg.id}>
            {cacheBoundaryIndex !== null &&
              msg.sequence === cacheBoundaryIndex + 1 && (
                <div className="relative flex h-4 items-center border-t border-amber-400">
                  <span className="absolute -top-2 left-2 bg-white px-1 font-mono text-[9px] uppercase tracking-widest text-amber-600 dark:bg-slate-900 dark:text-amber-400">
                    cache boundary
                  </span>
                </div>
              )}
            <button
              type="button"
              onClick={onClick}
              disabled={!onClick}
              className={`flex w-full min-w-0 items-center gap-2 border-b border-slate-50 px-4 py-1 text-left font-mono text-[10px] transition-colors dark:border-slate-900 ${onClick ? "hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer" : "cursor-default"} ${below ? "opacity-60" : ""}`}
            >
              <span className="w-6 shrink-0 text-slate-400">
                {msg.sequence}
              </span>
              {/* Role label hidden on narrow viewports — the source_kind
                  below already implies role (assistant_output, tool_result,
                  etc.), and dropping this saves ~56px on mobile. */}
              <span className="hidden w-14 shrink-0 text-slate-500 uppercase tracking-wider sm:inline">
                {msg.role}
              </span>
              <span className="w-24 shrink-0 truncate text-slate-600 dark:text-slate-300 sm:w-32">
                {msg.sourceKind}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={`block h-2 rounded-sm ${colorClass}`}
                  style={{ width: `${widthPct}%` }}
                />
              </span>
              {msg.cacheControl && (
                <span className="shrink-0 rounded-sm bg-amber-100 px-1 py-px text-[8px] uppercase tracking-widest text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  {msg.cacheControl}
                </span>
              )}
              {/* Block count + sha hidden on narrow — the bar width conveys
                  size, and the sha becomes a tap-to-see value in tier 3. */}
              <span className="hidden w-10 shrink-0 text-right text-slate-400 sm:inline">
                {msg.blocks.length > 0 ? `${msg.blocks.length}b` : ""}
              </span>
              <span className="hidden w-16 shrink-0 text-right text-slate-400 dark:text-slate-500 sm:inline">
                {shortSha(msg.textSha, 6)}
              </span>
            </button>
            {reasoningBlocks.map((block) => (
              <ReasoningBlockDisclosure
                key={block.id}
                block={block}
                messageSequence={msg.sequence}
                onLoadSnippet={onLoadSnippet}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};

export const ReasoningBlockDisclosure: FC<{
  block: ContentBlockEntity;
  messageSequence: number;
  onLoadSnippet: (sha: string) => Promise<SnippetEntity | null>;
}> = ({ block, messageSequence, onLoadSnippet }) => {
  const contentId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [snippet, setSnippet] = useState<SnippetEntity | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");

  useEffect(() => {
    if (!isOpen || snippet || status !== "idle" || !block.textSha) {
      return;
    }
    let cancelled = false;
    setStatus("loading");
    onLoadSnippet(block.textSha)
      .then((loadedSnippet) => {
        if (cancelled) {
          return;
        }
        if (!loadedSnippet) {
          setStatus("error");
          return;
        }
        setSnippet(loadedSnippet);
        setStatus("idle");
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [block.textSha, isOpen, onLoadSnippet, snippet, status]);

  return (
    <div className="border-b border-slate-100 bg-slate-50/70 px-4 pb-2 pl-12 dark:border-slate-900 dark:bg-slate-950/30">
      <button
        type="button"
        aria-controls={contentId}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
        className="group/reasoning flex w-full cursor-pointer items-center gap-2 rounded-sm border border-slate-200/80 bg-white/70 px-2.5 py-1.5 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 transition-colors hover:border-indigo-200 hover:bg-indigo-50/60 hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-400 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-400 dark:hover:border-indigo-500/40 dark:hover:bg-indigo-950/20 dark:hover:text-indigo-200"
      >
        <ChevronRightIcon
          size={12}
          className={`h-3 w-3 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}
        />
        <svg
          aria-hidden="true"
          className="h-3.5 w-3.5 shrink-0 text-indigo-500/80 dark:text-indigo-300/80"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="1.6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 18h6M10 22h4M8.5 15.5a6 6 0 117 0c-.7.55-1.2 1.22-1.48 2h-4.04c-.28-.78-.78-1.45-1.48-2z"
          />
        </svg>
        <span className="font-bold text-slate-600 dark:text-slate-300">
          Reasoning
        </span>
        <span className="hidden normal-case tracking-normal text-slate-400 sm:inline">
          thinking trace · msg {messageSequence}
        </span>
        <span className="ml-auto text-slate-400 dark:text-slate-500">
          {shortSha(block.textSha, 8)}
        </span>
      </button>
      {isOpen && (
        <div
          id={contentId}
          className="mt-2 overflow-hidden border border-slate-200 bg-white/80 shadow-inner dark:border-slate-800 dark:bg-slate-950/60"
        >
          {status === "loading" && (
            <div className="px-3 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
              Loading reasoning…
            </div>
          )}
          {status === "error" && (
            <div className="px-3 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-rose-500">
              Reasoning unavailable
            </div>
          )}
          {snippet && (
            <pre className="custom-scrollbar max-h-64 overflow-auto whitespace-pre-wrap break-words px-3 py-3 font-mono text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
              {snippet.content}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Snippet overlay — tier 3. Full text of a content-addressed snippet.
// ---------------------------------------------------------------------------
